import { randomBytes } from "@noble/hashes/utils.js";
import { JWK_CURVES, JWK_MEMBERS, JWK_SECRET_BYTES, MAX_JWK_KEYS, THUMBPRINT_MEMBERS } from "./algorithms";
import { formatSecret } from "./encoding";
import type { Jwk, JwkSet, JwkSettings } from "./types";

// Web Crypto exports JWK itself, so this asks it for the key and then writes the members a JWKS is read for.
export async function generateJwkSet(settings: JwkSettings): Promise<JwkSet> {
  const count = Math.min(Math.max(settings.count, 1), MAX_JWK_KEYS);
  // Whole seconds, so a set numbered by the clock is a run of consecutive stamps rather than one and its neighbours.
  const stamp = Math.floor(Date.now() / 1000) * 1000;
  const privateKeys: Jwk[] = [];
  const publicKeys: Jwk[] = [];

  for (let index = 0; index < count; index += 1) {
    const [privateJwk, publicJwk] = await jwkMaterial(settings.algorithm, settings.variant);
    // Off the public half where there is one, though RFC 7638 would hand back the same thumbprint for either.
    const kid = await keyIdFor(settings.keyId, publicJwk ?? privateJwk, stamp + index * 1000);
    privateKeys.push(withHeader(privateJwk, settings.algorithm, kid));
    if (publicJwk) publicKeys.push(withHeader(publicJwk, settings.algorithm, kid));
  }

  return {
    privateKeys,
    publicKeys,
    // One key is named by its thumbprint the way an SSH key is by its fingerprint; a set of them is named by nothing.
    thumbprint: count === 1 ? await thumbprint(publicKeys[0] ?? privateKeys[0], "SHA-256") : "",
  };
}

// The private half and, for the symmetric algorithms, no public one at all: the one key is both ends.
async function jwkMaterial(algorithm: string, variant: string): Promise<[Jwk, Jwk | null]> {
  const bytes = JWK_SECRET_BYTES[algorithm];
  if (bytes) return [{ kty: "oct", k: formatSecret(randomBytes(bytes), "base64url") }, null];

  const [params, usages] = jwkKeyParams(algorithm, variant);
  const pair = await crypto.subtle.generateKey(params, true, usages) as CryptoKeyPair;
  return [
    keyMaterial(await crypto.subtle.exportKey("jwk", pair.privateKey)),
    keyMaterial(await crypto.subtle.exportKey("jwk", pair.publicKey)),
  ];
}

// Each JWA name is one Web Crypto algorithm, with the usages that follow from what the key is for.
function jwkKeyParams(algorithm: string, variant: string): [KeyGenParams, KeyUsage[]] {
  // Checked ahead of the signature families, since "RSA-OAEP-256" opens with the letters "RS" as well.
  if (algorithm.startsWith("RSA-OAEP")) {
    // RSA-OAEP on its own is the SHA-1 pairing RFC 7518 names; the rest spell their hash out.
    const hash = algorithm === "RSA-OAEP" ? "SHA-1" : `SHA-${algorithm.slice("RSA-OAEP-".length)}`;
    return [rsaParams("RSA-OAEP", variant, hash), ["encrypt", "decrypt"]];
  }
  // An agreement key derives a secret rather than encrypt anything itself, and its curve is the Select's to pick.
  if (algorithm.startsWith("ECDH-ES")) {
    const params = variant === "X25519" ? { name: "X25519" } : { name: "ECDH", namedCurve: variant };
    return [params, ["deriveKey", "deriveBits"]];
  }
  if (algorithm.startsWith("RS") || algorithm.startsWith("PS")) {
    const name = algorithm.startsWith("PS") ? "RSA-PSS" : "RSASSA-PKCS1-v1_5";
    return [rsaParams(name, variant, `SHA-${algorithm.slice(2)}`), ["sign", "verify"]];
  }
  if (algorithm.startsWith("ES")) {
    return [{ name: "ECDSA", namedCurve: JWK_CURVES[algorithm] ?? "P-256" }, ["sign", "verify"]];
  }
  return [{ name: "Ed25519" }, ["sign", "verify"]];
}

function rsaParams(name: string, variant: string, hash: string): RsaHashedKeyGenParams {
  return { name, modulusLength: Number(variant), publicExponent: new Uint8Array([0x01, 0x00, 0x01]), hash };
}

type KeyGenParams = RsaHashedKeyGenParams | EcKeyGenParams | Algorithm;

// Web Crypto sends its own bookkeeping along with the key; a JWKS carries only what RFC 7517 names.
function keyMaterial(exported: JsonWebKey): Jwk {
  const jwk: Jwk = { kty: exported.kty ?? "" };
  for (const member of JWK_MEMBERS) {
    const value = (exported as Record<string, unknown>)[member];
    if (typeof value === "string") jwk[member] = value;
  }
  return jwk;
}

// What the key is for goes in front of what the key is, which is the order a JWKS is usually written in.
function withHeader(material: Jwk, algorithm: string, kid: string): Jwk {
  const { kty, ...rest } = material;
  return { kty, ...(kid ? { kid } : {}), use: jwkUse(algorithm), alg: algorithm, ...rest };
}

// Nothing has to be asked which a key is for: the JWE families of RFC 7518 encrypt, and every other name signs.
function jwkUse(algorithm: string): string {
  const encrypts = algorithm.startsWith("RSA-OAEP") || algorithm.startsWith("ECDH-ES") || algorithm.endsWith("KW");
  return encrypts ? "enc" : "sig";
}

async function keyIdFor(source: string, jwk: Jwk, at: number): Promise<string> {
  switch (source) {
    case "uuid":
      return crypto.randomUUID();
    case "timestamp":
      return `${at / 1000}`;
    case "iso":
      // Whole seconds is as fine as the numbering gets, so the milliseconds would be three zeroes on every key.
      return new Date(at).toISOString().replace(".000", "");
    case "sha256":
      return await thumbprint(jwk, "SHA-256");
    case "sha1":
      return await thumbprint(jwk, "SHA-1");
    default:
      return "";
  }
}

// RFC 7638: the required members, in lexicographic order, with no whitespace anywhere between them.
async function thumbprint(jwk: Jwk, hash: string): Promise<string> {
  const members = THUMBPRINT_MEMBERS[jwk.kty] ?? [];
  const canonical = `{${members.map((name) => `${JSON.stringify(name)}:${JSON.stringify(jwk[name] ?? "")}`)}}`;
  const digest = await crypto.subtle.digest(hash, new TextEncoder().encode(canonical));
  return formatSecret(new Uint8Array(digest), "base64url");
}
